import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  Banner, Button, List, ListItem, Select, StatusChip, TextField, useSnackbar,
} from '@meshble/molo';
import { api, must, type Domain, type Instance } from '../lib/api';
import { quandoRelativo } from '../lib/attivita';
import { Anello } from '../brand';
import { Info } from '../icons';
import { localeIntl, t } from '../lib/lingua';
import { ConPermesso } from '../permesso';
import { usePermessiSu } from '../session';

/**
 * Gli indirizzi di un'istanza: l'hostname di piattaforma, che c'è da solo, e
 * i domini custom che ci si collega. È un pannello della scheda dell'istanza.
 */

/** Gli stati di `domains.status` in `00010_domini.sql`. */
export const ETICHETTA_DOMINIO: Record<Domain['status'], string> = {
  pending: 'In collegamento',
  active: 'Risponde',
  error: 'Non collegato',
  removing: 'In rimozione',
};

export const TONO_DOMINIO: Record<Domain['status'], 'neutral' | 'running' | 'error' | 'success'> = {
  pending: 'running',
  active: 'success',
  error: 'error',
  removing: 'running',
};

/** Cosa fa ciascun tipo, con le parole di chi lo sceglie. */
export const TIPO_DOMINIO: Record<Domain['kind'], string> = {
  platform: 'Indirizzo della piattaforma',
  primary: 'Indirizzo principale',
  alias: 'Alias',
  redirect: 'Rimando',
};

const IN_CORSO = new Set<Domain['status']>(['pending', 'removing']);

export function useDomini(slug: string, istanzaId: string | undefined) {
  return useQuery({
    queryKey: ['domains', slug, istanzaId],
    enabled: Boolean(istanzaId),
    queryFn: () =>
      must(api.GET('/api/v1/orgs/{slug}/instances/{id}/domains', { params: { path: { slug, id: istanzaId! } } })),
    refetchInterval: (q) => ((q.state.data?.domains ?? []).some((d) => IN_CORSO.has(d.status)) ? 3000 : false),
  });
}

export function Domini({ slug, istanza }: { slug: string; istanza: Instance }) {
  const permessi = usePermessiSu(slug, { istanza: istanza.id });
  const qc = useQueryClient();
  const navigate = useNavigate();
  const avvisa = useSnackbar();
  const q = useDomini(slug, istanza.id);
  const domini = q.data?.domains ?? [];

  const [hostname, setHostname] = useState('');
  const [kind, setKind] = useState<'primary' | 'alias' | 'redirect'>('primary');
  const [redirectTo, setRedirectTo] = useState('');
  const [aggiungi, setAggiungi] = useState(false);

  const haPrimario = domini.some((d) => d.kind === 'primary');
  const tipoScelto = kind === 'primary' && haPrimario ? 'alias' : kind;
  const attiva = istanza.status === 'running';

  const invalida = () => qc.invalidateQueries({ queryKey: ['domains', slug, istanza.id] });
  const vaiAlJob = (jobId: string) => navigate({ to: '/orgs/$slug/jobs/$id', params: { slug, id: jobId } });

  const collega = useMutation({
    mutationFn: () =>
      must(
        api.POST('/api/v1/orgs/{slug}/instances/{id}/domains', {
          params: { path: { slug, id: istanza.id } },
          body: {
            hostname: hostname.trim().toLowerCase(),
            kind: tipoScelto,
            ...(tipoScelto === 'redirect' ? { redirectTo: redirectTo.trim() } : {}),
          },
        }),
      ),
    onSuccess: (d) => {
      invalida();
      setHostname('');
      setRedirectTo('');
      setAggiungi(false);
      avvisa(t('Sto collegando {host}: chiedo il certificato, ci vuole un minuto', { host: d.hostname }));
      if (d.jobId) vaiAlJob(d.jobId);
    },
  });

  const scollega = useMutation({
    mutationFn: (d: Domain) =>
      must(
        api.DELETE('/api/v1/orgs/{slug}/instances/{id}/domains/{domainId}', {
          params: { path: { slug, id: istanza.id, domainId: d.id } },
        }),
      ),
    onSuccess: (_r, d) => {
      invalida();
      avvisa(t('Sto scollegando {host}', { host: d.hostname }));
    },
  });

  // Un dominio fallito si riprova così com'è: la causa più comune è un DNS
  // non ancora propagato, che passa da sola.
  const riprova = useMutation({
    mutationFn: (d: Domain) =>
      must(
        api.POST('/api/v1/orgs/{slug}/instances/{id}/domains/{domainId}/retry', {
          params: { path: { slug, id: istanza.id, domainId: d.id } },
        }),
      ),
    onSuccess: (d) => {
      invalida();
      avvisa(t('Riprovo a collegare {host}', { host: d.hostname }));
      if (d.jobId) vaiAlJob(d.jobId);
    },
  });

  const hostnameOk = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/.test(hostname.trim().toLowerCase());
  const pronto = attiva && hostnameOk && (tipoScelto !== 'redirect' || redirectTo.trim().length > 0) && !collega.isPending;

  return (
    <div className="md-stack">
      {q.error && <Banner tone="error" title={t('Non riesco a leggere gli indirizzi')}>{q.error.message}</Banner>}

      {q.isSuccess && domini.length === 0 && (
        <p className="md-body-medium" style={{ margin: 0, color: 'var(--md-sys-color-on-surface-variant)' }}>
          {t('Questa istanza risponde solo in locale: il server non aveva ancora il certificato quando è nata. «Rigenera la configurazione», qui sotto, le dà il suo indirizzo.')}
        </p>
      )}

      {domini.length > 0 && (
        <List>
          {domini.map((d) => (
            <ListItem
              key={d.id}
              headline={<a href={d.url} target="_blank" rel="noreferrer">{d.hostname}</a>}
              supporting={
                d.status === 'error'
                  ? d.error || t('Il collegamento non è riuscito')
                  : d.kind === 'redirect'
                    ? t('{tipo} verso {verso}', { tipo: t(TIPO_DOMINIO[d.kind]), verso: d.redirectTo ?? '' })
                    : d.certNotAfter
                      ? t('{tipo} · certificato valido fino al {data}', {
                          tipo: t(TIPO_DOMINIO[d.kind]),
                          data: new Date(d.certNotAfter).toLocaleDateString(localeIntl()),
                        })
                      : t(TIPO_DOMINIO[d.kind])
              }
              trailing={
                <span className="md-stack__inline" style={{ alignItems: 'center' }}>
                  <StatusChip status={d.status} label={t(ETICHETTA_DOMINIO[d.status])} tone={TONO_DOMINIO[d.status]} />
                  {d.status === 'error' && attiva && (
                    <ConPermesso ha={permessi} serve="domain.manage">
                      <Button variant="text" disabled={riprova.isPending} onClick={() => riprova.mutate(d)}>
                        {t('Riprova')}
                      </Button>
                    </ConPermesso>
                  )}
                  {d.kind !== 'platform' && !IN_CORSO.has(d.status) && (
                    <ConPermesso ha={permessi} serve="domain.manage">
                      <Button variant="text" disabled={scollega.isPending} onClick={() => scollega.mutate(d)}>
                        {t('Scollega')}
                      </Button>
                    </ConPermesso>
                  )}
                </span>
              }
            />
          ))}
        </List>
      )}

      {domini.some((d) => d.cloudflareProxied) && (
        <Banner icon={<Info />} title={t('Un dominio passa da Cloudflare')}>
          {t('Perché funzioni tutto: SSL in modalità Full (strict), WebSockets attivi, e tieni presente che il proxy ferma gli upload oltre 100 MB e le richieste oltre 125 secondi.')}
        </Banner>
      )}

      {scollega.error && <Banner tone="error" title={t('Non scollegato')}>{scollega.error.message}</Banner>}
      {riprova.error && <Banner tone="error" title={t('Non riprovato')}>{riprova.error.message}</Banner>}
      {collega.error && <Banner tone="error" title={t('Non collegato')}>{collega.error.message}</Banner>}

      {!aggiungi ? (
        <div className="md-stack__inline">
          <ConPermesso ha={permessi} serve="domain.manage">
            <Button variant="tonal" disabled={!attiva} onClick={() => setAggiungi(true)}>{t('Collega un dominio tuo')}</Button>
          </ConPermesso>
        </div>
      ) : (
        <form
          className="md-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (pronto) collega.mutate();
          }}
        >
          <TextField
            label={t('Dominio')}
            value={hostname}
            autoFocus
            onChange={(e) => setHostname(e.target.value.toLowerCase())}
            error={hostname.length > 0 && !hostnameOk}
            help={
              hostname.length > 0 && !hostnameOk
                ? t('Serve il nome completo, es. «shop.acme.it».')
                : istanza.url
                  ? t('Prima fai puntare il record A del dominio a {server}, o un CNAME a {host}.', {
                      server: istanza.serverName ?? t('questo server'),
                      host: istanza.url.replace('https://', ''),
                    })
                  : t('Prima fai puntare il record A del dominio a {server}.', { server: istanza.serverName ?? t('questo server') })
            }
          />
          <Select
            label={t('Tipo')}
            value={tipoScelto}
            options={[
              { value: 'primary', label: t('Indirizzo principale — quello che Odoo scrive nelle email'), disabled: haPrimario },
              { value: 'alias', label: t('Alias — risponde con lo stesso Odoo') },
              { value: 'redirect', label: t('Rimando — manda a un altro indirizzo') },
            ]}
            onChange={(e) => setKind(e.target.value as 'primary' | 'alias' | 'redirect')}
          />
          {tipoScelto === 'redirect' && (
            <TextField
              label={t('Verso')}
              value={redirectTo}
              onChange={(e) => setRedirectTo(e.target.value)}
              help={t('Un dominio o un indirizzo completo, es. «acme.it» o «https://acme.it/shop».')}
            />
          )}
          <div className="md-stack__inline" style={{ justifyContent: 'flex-end' }}>
            <Button variant="text" onClick={() => setAggiungi(false)}>{t('Annulla')}</Button>
            <Button variant="filled" icon={collega.isPending ? <Anello /> : undefined} disabled={!pronto} onClick={() => collega.mutate()}>
              {collega.isPending ? t('Verifico il DNS…') : t('Collega')}
            </Button>
          </div>
        </form>
      )}

      {q.isSuccess && domini.length > 0 && (
        <p className="md-body-small" style={{ margin: 0, color: 'var(--md-sys-color-on-surface-variant)' }}>
          {t('I certificati si rinnovano da soli. Ultimo aggiornamento: {quando}.', { quando: quandoRelativo(new Date(q.dataUpdatedAt).toISOString()) })}
        </p>
      )}
    </div>
  );
}
